// packages/shared/api-client/endpoints/events.js
// Calendar and events API endpoints

import { baseApi } from "../baseApi";

/**
 * Event API endpoints
 * Provides event management, RSVP tracking, and attendance
 */
const eventApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all events with filtering
    getEvents: builder.query({
      query: (params = {}) => ({
        url: "/api/events",
        params, // { eventType?, visibility?, upcoming?, page?, limit? }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Event", id })),
              { type: "Event", id: "LIST" },
            ]
          : [{ type: "Event", id: "LIST" }],
    }),

    // Get specific event by ID
    getEventById: builder.query({
      query: (id) => `/api/events/${id}`,
      providesTags: (result, error, id) => [{ type: "Event", id }],
    }),

    // Create new event
    createEvent: builder.mutation({
      query: (eventData) => ({
        url: "/api/events",
        method: "POST",
        body: eventData, // { title, description, eventType, startTime, endTime, location, isRecurring?, recurrenceRule?, maxAttendees?, rsvpEnabled? }
      }),
      invalidatesTags: [{ type: "Event", id: "LIST" }],
    }),

    // Update event
    updateEvent: builder.mutation({
      query: ({ id, ...eventData }) => ({
        url: `/api/events/${id}`,
        method: "PUT",
        body: eventData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
      ],
    }),

    // Delete event
    deleteEvent: builder.mutation({
      query: (id) => ({
        url: `/api/events/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Event", id: "LIST" }],
    }),

    // RSVP to event
    rsvpToEvent: builder.mutation({
      query: ({ id, status }) => ({
        url: `/api/events/${id}/rsvp`,
        method: "POST",
        body: { status }, // 'going', 'maybe', 'not_going'
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
      ],
    }),

    // Cancel RSVP
    cancelRsvp: builder.mutation({
      query: (id) => ({
        url: `/api/events/${id}/rsvp`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Event", id },
        { type: "Event", id: "LIST" },
      ],
    }),

    // Get event RSVPs (for organizers)
    getEventRsvps: builder.query({
      query: (id) => `/api/events/${id}/rsvps`,
      providesTags: (result, error, id) => [{ type: "Event", id }],
    }),

    // Get event attendees (users who RSVP'd "going")
    getEventAttendees: builder.query({
      query: (id) => `/api/events/${id}/attendees`,
      providesTags: (result, error, id) => [{ type: "Event", id }],
    }),
  }),
});

// Export hooks for usage in components
export const useGetEventsQuery = eventApi.useGetEventsQuery;
export const useLazyGetEventsQuery = eventApi.useLazyGetEventsQuery;
export const useGetEventByIdQuery = eventApi.useGetEventByIdQuery;
export const useCreateEventMutation = eventApi.useCreateEventMutation;
export const useUpdateEventMutation = eventApi.useUpdateEventMutation;
export const useDeleteEventMutation = eventApi.useDeleteEventMutation;
export const useRsvpToEventMutation = eventApi.useRsvpToEventMutation;
export const useCancelRsvpMutation = eventApi.useCancelRsvpMutation;
export const useGetEventRsvpsQuery = eventApi.useGetEventRsvpsQuery;
export const useGetEventAttendeesQuery = eventApi.useGetEventAttendeesQuery;
